import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IoChevronBack } from 'react-icons/io5';
import { FiSearch } from 'react-icons/fi';

import SortButton from '../../lib/SortButton';
import styles from '../../styles/consult.module.scss';

function getSortValue(item, column) {
  if (column.sortValue) return column.sortValue(item);
  const raw = item?.[column.key];
  if (raw == null) return '';
  if (typeof raw === 'object') return raw.name || raw.title || raw.label || '';
  return raw;
}

function compareValues(a, b, column) {
  const av = getSortValue(a, column);
  const bv = getSortValue(b, column);

  if (column.sortType === 'date') {
    const aDate = av ? new Date(av) : new Date(0);
    const bDate = bv ? new Date(bv) : new Date(0);
    if (aDate < bDate) return -1;
    if (aDate > bDate) return 1;
    return 0;
  }

  if (column.sortType === 'number' || (typeof av === 'number' && typeof bv === 'number')) {
    return (Number(av) || 0) - (Number(bv) || 0);
  }

  return String(av).localeCompare(String(bv), 'fr', { sensitivity: 'base' });
}

/**
 * Generic list page for consultation sections — style aligned with Projects.
 */
export default function ContentList({
  title,
  listSelector,
  fetchAction,
  getLabel,
  getSubLabel,
  basePath,
  columns,
  cardMode = false,
  getImage,
  getTags,
  headerAction,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const list = useSelector(listSelector) || [];
  const [search, setSearch] = useState('');

  const initSortState = useMemo(() => {
    const state = {};
    (columns || []).forEach((c) => {
      state[c.key] = true;
    });
    return state;
  }, [columns]);

  const [sortByReverse, setSortByReverse] = useState(initSortState);
  const [filteredList, setFilteredList] = useState([]);

  useEffect(() => {
    fetchAction(dispatch);
  }, [dispatch, fetchAction]);

  useEffect(() => {
    setSortByReverse(initSortState);
  }, [initSortState]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFilteredList([...(list || [])]);
      return;
    }
    setFilteredList(
      (list || []).filter((item) => {
        const label = (getLabel(item) || '').toLowerCase();
        const sub = (getSubLabel?.(item) || '').toLowerCase();
        const cols = (columns || [])
          .map((c) => String(getSortValue(item, c) || '').toLowerCase())
          .join(' ');
        return label.includes(q) || sub.includes(q) || cols.includes(q);
      }),
    );
  }, [list, search, getLabel, getSubLabel, columns]);

  const sortList = useCallback((type, isReverse) => {
    const column = (columns || []).find((c) => c.key === type);
    if (!column) return;
    const next = [...filteredList].sort((a, b) => compareValues(a, b, column));
    setFilteredList(isReverse ? next.reverse() : next);
    setSortByReverse({ ...initSortState, [type]: isReverse });
  }, [columns, filteredList, initSortState]);

  const colCount = columns?.length || 1;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2>{title}</h2>
        <div className={styles.headerActions}>
          {headerAction}
          {!cardMode && (
            <div className={styles.search}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher"
              />
              <FiSearch />
            </div>
          )}
        </div>
      </header>

      {cardMode ? (
        <div className={styles.cards}>
          {filteredList.map((item) => (
            <Link
              key={item._id}
              to={`${basePath}/${item._id}`}
              className={styles.card}
            >
              {getImage?.(item) && (
                <img className={styles.thumb} src={getImage(item)} alt="" />
              )}
              <div className={styles.body}>
                <h3>{getLabel(item)}</h3>
                {getSubLabel?.(item) && <p>{getSubLabel(item)}</p>}
                {getTags?.(item)?.length > 0 && (
                  <div className={styles.tags}>
                    {getTags(item).map((t) => <span key={t}>{t}</span>)}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <>
          <div className={styles.sort} style={{ '--cols': colCount }}>
            <div className={`${styles.row} ${styles.labels}`}>
              {(columns || [{ key: 'name', label: 'Nom' }]).map((c) => (
                <SortButton
                  key={c.key}
                  name={c.label}
                  handleClick={() => sortList(c.key, !sortByReverse[c.key])}
                  state={sortByReverse[c.key]}
                  className={`${styles.col}${c.desktop ? ` ${styles.desktop}` : ''}`}
                />
              ))}
            </div>
          </div>
          <div className={styles.list} style={{ '--cols': colCount }}>
            {filteredList.map((item) => (
              <button
                key={item._id}
                type="button"
                className={styles.row}
                onClick={() => navigate(`${basePath}/${item._id}`)}
              >
                {(columns || [{ key: 'name', render: getLabel }]).map((c, index) => (
                  <div
                    key={c.key}
                    className={`${styles.col}${c.desktop ? ` ${styles.desktop}` : ''}`}
                  >
                    <div className={index === 0 ? styles.name : undefined}>
                      {c.render ? c.render(item) : getLabel(item)}
                    </div>
                  </div>
                ))}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ContentDetail({
  title,
  fetchAction,
  itemSelector,
  backPath,
  children,
}) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const item = useSelector(itemSelector);

  useEffect(() => {
    if (id) fetchAction(dispatch, id);
  }, [dispatch, id, fetchAction]);

  return (
    <div className={styles.detail}>
      <Link to={backPath} className={styles.back}>
        <IoChevronBack />
        Retour à la liste
      </Link>
      <div className={styles.header}>
        <h2>{typeof title === 'function' ? title(item) : title}</h2>
      </div>
      {children(item)}
    </div>
  );
}
